// App.js - FIXED: champConfig Error & Configurable Item Despawn
import React, { useState, useRef, useEffect, useReducer } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars } from '@react-three/drei';
import * as THREE from 'three';
import './App.css';
import AutoUpdater from './AutoUpdater';

// --- FIREBASE IMPORTS ---
import { db } from './firebaseConfig';
import { collection, query, orderBy, limit, getDocs, doc, setDoc, getDoc, deleteDoc } from "firebase/firestore";

import { GAME_CONFIG, randomRange } from './GameConfig';
import { generateAIResponse } from './AILogic'; 
import { ClickMarker, SkillMarker, WindWall, Projectile, XerathW, LuxUlt, MasteryEmote, ItemOrb, BansheeShield, GhostEffect, KarthusUltEffect, JhinUltIndicator } from './GameAssets';
import { Player } from './Player';

const getLeagueInfo = (score) => {
    if (score < 15) return { name: "DEMİR", color: "#58524d" };       
    if (score < 30) return { name: "BRONZ", color: "#cd7f32" };       
    if (score < 50) return { name: "GÜMÜŞ", color: "#c0c0c0" };       
    if (score < 80) return { name: "ALTIN", color: "#ffd700" };      
    if (score < 110) return { name: "PLATİN", color: "#00ffb3" };     
    if (score < 150) return { name: "ZÜMRÜT", color: "#00b33c" };     
    if (score < 200) return { name: "ELMAS", color: "#00ccff" };      
    if (score < 260) return { name: "USTA", color: "#d255ff" };       
    if (score < 330) return { name: "ÜSTAT", color: "#ff3333" };      
    return { name: "ŞAMPİYONLUK", color: "#ffffaa", glow: true };     
};

const getWIcon = (champ) => { 
    if (champ === 'leblanc') return '🔮'; 
    if (champ === 'zed') return '👤'; 
    if (champ === 'yasuo') return '💨'; 
    return 'W'; 
};

// --- CHAT WIDGET ---
const ChatWidget = ({ visible, history, setHistory, gameContext }) => {
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false); 
    const endRef = useRef(null);

    const handleSend = async () => {
        if (!input.trim()) return;
        setHistory(prev => [...prev, { sender: 'user', text: input }]);
        const userMsg = input; 
        setInput("");
        setIsTyping(true);
        try {
            const reply = await generateAIResponse(userMsg, gameContext);
            setHistory(prev => [...prev, { sender: 'ai', text: reply }]);
        } catch (err) {
            setHistory(prev => [...prev, { sender: 'ai', text: "Bağlantı koptu brom..." }]);
        } finally {
            setIsTyping(false);
        }
    };

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [history, visible, isTyping]);

    if (!visible) return null;

    return (
        <div className="chat-widget">
            <div className="chat-header">
                <span style={{ fontSize: '1.5rem', marginRight: '5px' }}>🤖</span> 
                DestekMalDestek v3.0
            </div>
            <div className="chat-messages">
                {history.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.sender}`}>
                        {msg.text}
                    </div>
                ))}
                {isTyping && <div className="message ai" style={{fontStyle:'italic', color:'#888'}}>yazıyor...</div>}
                <div ref={endRef} />
            </div>
            <div className="chat-input-area">
                <input 
                    className="chat-input"
                    placeholder="Taktik iste veya söv..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <button className="chat-send-btn" onClick={handleSend}>Yolla</button>
            </div>
        </div>
    );
};

// --- GAME SCENE ---
const GameScene = ({ setScore, setHighScore, score, highScore, setGameOver, setHealth, health, activeBuffs, setActiveBuffs, setShowDamageFlash, flashCooldownRef, wStateRef, zhonyaStateRef, isPaused, isGameOver, flashKey, selectedChamp, forceUpdate }) => {
  const [targetPos, setTargetPos] = useState(new THREE.Vector3(0, 0.5, 0));
  const [clickMarker, setClickMarker] = useState({ pos: new THREE.Vector3(), visible: false, key: 0 });
  
  const champConfig = GAME_CONFIG.CHAMPIONS[selectedChamp];

  const [flashTrigger, setFlashTrigger] = useState({ active: false, target: new THREE.Vector3() });
  const [wTrigger, setWTrigger] = useState({ active: false, mode: 'dash', target: new THREE.Vector3() });
  const [isZhonyaActive, setIsZhonyaActive] = useState(false); 
  
  const [showMastery, setShowMastery] = useState(false);
  const lastMasteryScore = useRef(0);

  const [items, setItems] = useState([]); 
  const ghostTimerRef = useRef(0);      
  const gameTimeRef = useRef(0); 

  const wMechanicsRef = useRef({ shadowPos: new THREE.Vector3(), shadowTarget: new THREE.Vector3(), isShadowMoving: false, wallPos: new THREE.Vector3(), wallTarget: new THREE.Vector3(), isWallMoving: false, wallRot: 0 });
  const [wSkillEntity, setWSkillEntity] = useState({ visible: false, type: 'pad' });
  const [yasuoWall, setYasuoWall] = useState({ visible: false, expire: 0 });
  
  const [projectiles, setProjectiles] = useState([]);
  const [xerathWs, setXerathWs] = useState([]); 
  const [luxUlts, setLuxUlts] = useState([]); 
  
  const [karthusUlt, setKarthusUlt] = useState(false); 
  const [jhinIndicator, setJhinIndicator] = useState({ 
      visible: false, 
      position: new THREE.Vector3(-50, 0.5, -50), 
      rotation: 0, 
      target: null 
  }); 
  const bossCycleRef = useRef({ lastBoss: null, lastTriggerScore: 0 });
  const activeTimersRef = useRef({ jhinTimeout: null, jhinInterval: null, karthusTimeout: null });
  
  const activeBuffsRef = useRef(activeBuffs);
  useEffect(() => { activeBuffsRef.current = activeBuffs; }, [activeBuffs]);
  
  const playerPosRef = useRef(new THREE.Vector3());
  const mousePosRef = useRef(new THREE.Vector3());
  
  const lastSpawnTime = useRef(0);
  const lastXerathTime = useRef(0); 
  const lastLuxTime = useRef(0); 
  const lastScoreTime = useRef(0);
  const lastItemTime = useRef({ hp: 0, banshee: 0, ghost: 0 });

  useEffect(() => {
      return () => {
          if (activeTimersRef.current.jhinTimeout) clearTimeout(activeTimersRef.current.jhinTimeout);
          if (activeTimersRef.current.jhinInterval) clearInterval(activeTimersRef.current.jhinInterval);
          if (activeTimersRef.current.karthusTimeout) clearTimeout(activeTimersRef.current.karthusTimeout);
      };
  }, []);

  useEffect(() => {
    if (score > 0 && score % GAME_CONFIG.BOSS_TRIGGER_SCORE === 0 && score !== bossCycleRef.current.lastTriggerScore) {
        bossCycleRef.current.lastTriggerScore = score;
        const cycleIndex = score / GAME_CONFIG.BOSS_TRIGGER_SCORE; 
        let bossToSpawn = null;
        if (cycleIndex % 2 !== 0) {
            bossToSpawn = Math.random() < 0.5 ? 'jhin' : 'karthus';
        } else {
            if (bossCycleRef.current.lastBoss === 'jhin') bossToSpawn = 'karthus';
            else if (bossCycleRef.current.lastBoss === 'karthus') bossToSpawn = 'jhin';
            else bossToSpawn = 'jhin'; 
        }
        bossCycleRef.current.lastBoss = bossToSpawn;
        triggerBossFight(bossToSpawn);
    }
  }, [score]);

  const triggerBossFight = (bossType) => {
      if (bossType === 'jhin') startJhinUlt();
      else if (bossType === 'karthus') startKarthusUlt();
  };

  const startJhinUlt = () => {
    const jhinPos = new THREE.Vector3(-50, 0.5, -50);
    const fixedTarget = playerPosRef.current ? playerPosRef.current.clone() : new THREE.Vector3(0, 0.5, 0);
    const coneDir = new THREE.Vector3().subVectors(fixedTarget, jhinPos).normalize();

    setJhinIndicator({ 
        visible: true, 
        position: jhinPos, 
        target: fixedTarget,
        id: Date.now()
    });

    activeTimersRef.current.jhinTimeout = setTimeout(() => {
        if (isGameOver || isPaused) {
             setJhinIndicator(p => ({...p, visible: false}));
             return;
        }

        let shotsFired = 0;
        activeTimersRef.current.jhinInterval = setInterval(() => {
            if (isGameOver || isPaused) { 
                clearInterval(activeTimersRef.current.jhinInterval); 
                setJhinIndicator(p => ({...p, visible: false})); 
                return; 
            }
            
            shotsFired++;
            const isCrit = shotsFired === 4; 
            
            const currentPlayerPos = playerPosRef.current.clone();
            const toPlayerDir = new THREE.Vector3().subVectors(currentPlayerPos, jhinPos).normalize();
            const dot = coneDir.dot(toPlayerDir);
            
            if (dot > 0.92) {
                const velocity = new THREE.Vector3()
                    .subVectors(currentPlayerPos, jhinPos)
                    .normalize()
                    .multiplyScalar(GAME_CONFIG.JHIN_BULLET_SPEED * 1.8);
                
                setProjectiles(prev => [...prev, { 
                    id: Math.random(), 
                    startPos: jhinPos.clone(), 
                    velocity: velocity,
                    isJhin: true,
                    isCrit: isCrit
                }]);
            }

            if (shotsFired >= GAME_CONFIG.JHIN_TOTAL_SHOTS) {
                clearInterval(activeTimersRef.current.jhinInterval);
                setTimeout(() => {
                     setJhinIndicator(p => ({...p, visible: false}));
                }, 800);
            }
        }, GAME_CONFIG.JHIN_SHOT_DELAY);

    }, GAME_CONFIG.JHIN_PREPARE_TIME);
  };

  const startKarthusUlt = () => {
      setKarthusUlt(true); 
      activeTimersRef.current.karthusTimeout = setTimeout(() => {
          setKarthusUlt(false);
          if (isGameOver || isPaused) return;
          if (zhonyaStateRef.current.active) return;
          handleHit(1); 
      }, GAME_CONFIG.KARTHUS_CHANNEL_TIME);
  };

  // --- GAME LOOP OPTIMIZED ---
  useFrame((state, delta) => {
      if (isGameOver || isPaused) return;
      
      gameTimeRef.current += delta;
      const gameTime = gameTimeRef.current;
      const now = Date.now();
      
      if (gameTime < 2.0) { 
          lastSpawnTime.current = gameTime; 
          lastXerathTime.current = gameTime; 
          lastLuxTime.current = gameTime; 
          lastScoreTime.current = gameTime;
          lastItemTime.current = { hp: gameTime, banshee: gameTime, ghost: gameTime };
      }

      // Physics logic - Config tabanlı hızlar
      if (wMechanicsRef.current.isShadowMoving && wSkillEntity.visible) { 
          const mech = wMechanicsRef.current; 
          const dir = new THREE.Vector3().subVectors(mech.shadowTarget, mech.shadowPos); 
          const step = (champConfig.SHADOW_SPEED || 25) * delta; 
          if (dir.length() <= step) { mech.shadowPos.copy(mech.shadowTarget); mech.isShadowMoving = false; } 
          else { dir.normalize().multiplyScalar(step); mech.shadowPos.add(dir); } 
      }
      
      if (wMechanicsRef.current.isWallMoving && yasuoWall.visible) { 
          const mech = wMechanicsRef.current; 
          const dir = new THREE.Vector3().subVectors(mech.wallTarget, mech.wallPos); 
          const step = (champConfig.WALL_SPEED || 10) * delta; 
          if (dir.length() <= step) { mech.wallPos.copy(mech.wallTarget); mech.isWallMoving = false; } 
          else { dir.normalize().multiplyScalar(step); mech.wallPos.add(dir); } 
      }
      
      // Cooldown checks
      if (zhonyaStateRef.current.active && now > zhonyaStateRef.current.endTime) { zhonyaStateRef.current.active = false; setIsZhonyaActive(false); }
      if (yasuoWall.visible && now > yasuoWall.expire) setYasuoWall(prev => ({ ...prev, visible: false }));
      if (wStateRef.current.activeState && now > wStateRef.current.stateExpire) { 
          setWSkillEntity(prev => ({ ...prev, visible: false })); 
          wStateRef.current.activeState = false; 
          wStateRef.current.isOnCooldown = true; 
          wStateRef.current.cooldownEnd = now + champConfig.W_COOLDOWN; 
          forceUpdate(); 
      }
      if (activeBuffs.ghost && now > ghostTimerRef.current) { setActiveBuffs(prev => ({ ...prev, ghost: false })); }
      
      // Cooldown UI Update
      if (wStateRef.current.isOnCooldown && now > wStateRef.current.cooldownEnd) {
          wStateRef.current.isOnCooldown = false;
          forceUpdate();
      }

      // Item Spawns
      if (gameTime - lastItemTime.current.hp > GAME_CONFIG.SPAWN_RATES.HP) { spawnItem('hp'); lastItemTime.current.hp = gameTime; }
      if (gameTime - lastItemTime.current.banshee > GAME_CONFIG.SPAWN_RATES.BANSHEE) { spawnItem('banshee'); lastItemTime.current.banshee = gameTime; }
      if (gameTime - lastItemTime.current.ghost > GAME_CONFIG.SPAWN_RATES.GHOST) { spawnItem('ghost'); lastItemTime.current.ghost = gameTime; }

      // ITEM LOGIC: COLLISION + DESPAWN (CONFIG SÜRESİ İLE)
      if (items.length > 0) {
          let hasChanges = false;
          const remainingItems = [];
          
          // GAME_CONFIG.ITEM_DESPAWN_TIME kullanarak süreyi al
          const despawnTime = GAME_CONFIG.ITEM_DESPAWN_TIME || 5000;

          for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (!item.position) continue;
              
              // 1. Süre Kontrolü (Despawn)
              const age = now - item.createdAt;
              if (age > despawnTime) {
                  hasChanges = true; // Süresi doldu, silinecek
                  continue; 
              }

              // 2. Çarpışma Kontrolü (Pickup)
              if (playerPosRef.current && item.position.distanceTo(playerPosRef.current) < 2.5) {
                  hasChanges = true; // Alındı, silinecek
                  if (item.type === 'hp') { setHealth(prev => Math.min(prev + 1, GAME_CONFIG.MAX_HP)); } 
                  else if (item.type === 'banshee') { setActiveBuffs(p => ({...p, banshee: true})); } 
                  else if (item.type === 'ghost') { setActiveBuffs(p => ({...p, ghost: true})); ghostTimerRef.current = Date.now() + GAME_CONFIG.GHOST_DURATION; }
              } else {
                  remainingItems.push(item); // Hala yerde
              }
          }
          
          if (hasChanges) { setItems(remainingItems); }
      }

      // Score
      if (gameTime - lastScoreTime.current > 0.5) { 
          setScore(s => { 
              const newScore = s + 1; 
              if (selectedChamp === 'yasuo' && newScore > 0 && newScore % 30 === 0 && newScore !== lastMasteryScore.current) { 
                  lastMasteryScore.current = newScore; setShowMastery(true); setTimeout(() => setShowMastery(false), 2500); 
              } 
              return newScore; 
          }); 
          lastScoreTime.current = gameTime; 
      }
      
      // Projectile Spawns
      const qSpawnInterval = Math.max(0.3, 1.0 - (gameTime * 0.01)); 
      if (gameTime - lastSpawnTime.current > qSpawnInterval) { spawnProjectile(); lastSpawnTime.current = gameTime; }
      const wSpawnInterval = Math.max(0.6, 1.8 - (gameTime * 0.02)); 
      if (gameTime - lastXerathTime.current > wSpawnInterval) { if (Math.random() < 0.35) spawnXerathW(); lastXerathTime.current = gameTime; }
      const luxInterval = 3.0; 
      if (gameTime - lastLuxTime.current > luxInterval) { if (Math.random() < 0.8) spawnLuxUlt(); lastLuxTime.current = gameTime; }
  });

  const spawnItem = (type) => {
      const spawnRadius = 20; 
      let centerX = 0; let centerZ = 0;
      if (playerPosRef.current) { centerX = playerPosRef.current.x; centerZ = playerPosRef.current.z; }
      const x = centerX + randomRange(-spawnRadius, spawnRadius);
      const z = centerZ + randomRange(-spawnRadius, spawnRadius);
      const limit = (GAME_CONFIG.MAP_SIZE / 2) - 5;
      const finalX = THREE.MathUtils.clamp(x, -limit, limit);
      const finalZ = THREE.MathUtils.clamp(z, -limit, limit);
      const posVector = new THREE.Vector3(finalX, 0.5, finalZ);
      
      // createdAt EKLENDİ
      setItems(prev => [...prev, { 
          id: Math.random(), 
          type: type, 
          position: posVector, 
          createdAt: Date.now() 
      }]);
  };

  const spawnProjectile = () => { const angle = Math.random() * Math.PI * 2; const start = new THREE.Vector3(Math.cos(angle) * GAME_CONFIG.MAP_SIZE * 0.8, 0.5, Math.sin(angle) * GAME_CONFIG.MAP_SIZE * 0.8); const target = playerPosRef.current.clone(); target.x += randomRange(-3, 3); target.z += randomRange(-3, 3); const velocity = new THREE.Vector3().subVectors(target, start).normalize().multiplyScalar(GAME_CONFIG.PROJECTILE_SPEED); setProjectiles(prev => [...prev, { id: Math.random(), startPos: start, velocity: velocity }]); };
  const spawnXerathW = () => { const target = playerPosRef.current.clone(); target.x += randomRange(-1.5, 1.5); target.z += randomRange(-1.5, 1.5); const limit = GAME_CONFIG.MAP_SIZE / 2 - 2; target.x = THREE.MathUtils.clamp(target.x, -limit, limit); target.z = THREE.MathUtils.clamp(target.z, -limit, limit); setXerathWs(prev => [...prev, { id: Math.random(), position: target }]); };
  const spawnLuxUlt = () => { const center = playerPosRef.current.clone(); setLuxUlts(prev => [...prev, { id: Math.random(), position: center, rotation: Math.random() * Math.PI * 2 }]); };
  const removeProjectile = (id) => setProjectiles(prev => prev.filter(p => p.id !== id)); const removeXerathW = (id) => setXerathWs(prev => prev.filter(w => w.id !== id)); const removeLuxUlt = (id) => setLuxUlts(prev => prev.filter(l => l.id !== id));
  
  const handleHit = (damageAmount = 1) => { 
      if (zhonyaStateRef.current.active) return; 
      if (activeBuffsRef.current.banshee) { setActiveBuffs(prev => ({ ...prev, banshee: false })); return; }
      setHealth(prevHealth => {
          if (prevHealth <= 0) return 0;
          const newHealth = prevHealth - damageAmount; 
          setShowDamageFlash(true);
          setTimeout(() => setShowDamageFlash(false), 200); 
          if (newHealth <= 0) { setGameOver(true); return 0; }
          return newHealth;
      });
  };

  const onPointerDown = (e) => { if (isPaused || isGameOver || isZhonyaActive) return; if (e.nativeEvent.button === 2) { const newTarget = new THREE.Vector3(e.point.x, 0.5, e.point.z); setTargetPos(newTarget); setClickMarker({ pos: newTarget, visible: true, key: Math.random() }); } };
  const onPointerMove = (e) => { mousePosRef.current.copy(e.point); };
  
  useEffect(() => {
    const handleKey = (e) => {
      if (isPaused || isGameOver) return;
      const key = e.key.toLowerCase();
      const now = Date.now();

      if (key === flashKey && !zhonyaStateRef.current.active) { 
          if (now - flashCooldownRef.current.lastUsed > GAME_CONFIG.FLASH_COOLDOWN) { 
              setFlashTrigger({ active: true, target: mousePosRef.current.clone() }); 
              flashCooldownRef.current.lastUsed = now; 
              forceUpdate(); 
          } 
      }
      
      // LEBLANC W (ORİJİNAL - CONFIG'E GÖRE)
      if (key === 'w' && !zhonyaStateRef.current.active) {
          if (selectedChamp === 'leblanc') { 
              if (!wStateRef.current.activeState && !wStateRef.current.isOnCooldown) { 
                  wMechanicsRef.current.shadowPos.copy(playerPosRef.current); 
                  setWSkillEntity({ visible: true, type: 'pad' }); 
                  
                  wStateRef.current.stateExpire = now + champConfig.W_DURATION; 
                  wStateRef.current.activeState = true; 
                  
                  setWTrigger({ active: true, mode: 'dash', target: mousePosRef.current.clone() }); 
                  forceUpdate(); 
              } else if (wStateRef.current.activeState) { 
                  setWTrigger({ active: true, mode: 'return', target: wMechanicsRef.current.shadowPos }); 
                  setWSkillEntity(prev => ({ ...prev, visible: false })); 
                  wStateRef.current.activeState = false; 
                  wStateRef.current.isOnCooldown = true; 
                  wStateRef.current.cooldownEnd = now + champConfig.W_COOLDOWN; 
                  forceUpdate(); 
              } 
          } 
          else if (selectedChamp === 'zed') { 
              if (!wStateRef.current.activeState && !wStateRef.current.isOnCooldown) { 
                  const dir = new THREE.Vector3().subVectors(mousePosRef.current, playerPosRef.current); 
                  const dist = Math.min(dir.length(), champConfig.W_RANGE); 
                  dir.normalize().multiplyScalar(dist); 
                  wMechanicsRef.current.shadowPos.copy(playerPosRef.current); 
                  wMechanicsRef.current.shadowTarget.copy(playerPosRef.current.clone().add(dir)); 
                  wMechanicsRef.current.isShadowMoving = true; 
                  setWSkillEntity({ visible: true, type: 'shadow' }); 
                  wStateRef.current.stateExpire = now + champConfig.W_DURATION; 
                  wStateRef.current.activeState = true; 
                  forceUpdate(); 
              } else if (wStateRef.current.activeState) { 
                  setWTrigger({ active: true, mode: 'teleport', target: null }); 
                  setWSkillEntity(prev => ({ ...prev, visible: false })); 
                  wStateRef.current.activeState = false; 
                  wStateRef.current.isOnCooldown = true; 
                  wStateRef.current.cooldownEnd = now + champConfig.W_COOLDOWN; 
                  forceUpdate(); 
              } 
          } 
          else if (selectedChamp === 'yasuo') { 
              if (!wStateRef.current.isOnCooldown) { 
                  const dx = mousePosRef.current.x - playerPosRef.current.x; 
                  const dz = mousePosRef.current.z - playerPosRef.current.z; 
                  const angle = Math.atan2(dx, dz); 
                  const absDx = Math.abs(dx); 
                  const absDz = Math.abs(dz); 
                  const isCardinal = (absDx > absDz * 2.5) || (absDz > absDx * 2.5); 
                  let finalRot = angle; 
                  if (!isCardinal) { finalRot += Math.PI / 2; } 
                  const spawnOffset = 2.0; 
                  const start = playerPosRef.current.clone().add(new THREE.Vector3(Math.sin(angle), 0, Math.cos(angle)).multiplyScalar(spawnOffset)); 
                  wMechanicsRef.current.wallPos.copy(start); 
                  wMechanicsRef.current.wallTarget.copy(start.clone().add(new THREE.Vector3(Math.sin(angle), 0, Math.cos(angle)).multiplyScalar(champConfig.WALL_DIST))); 
                  wMechanicsRef.current.wallRot = finalRot; 
                  wMechanicsRef.current.isWallMoving = true; 
                  setYasuoWall({ visible: true, expire: now + champConfig.W_DURATION }); 
                  wStateRef.current.isOnCooldown = true; 
                  wStateRef.current.cooldownEnd = now + champConfig.W_COOLDOWN; 
                  setWTrigger({ active: true, mode: 'wall', target: null }); 
                  forceUpdate(); 
              } 
          }
      }
      if (key === 's' && !zhonyaStateRef.current.active) { if (playerPosRef.current) { setTargetPos(playerPosRef.current.clone()); setClickMarker(prev => ({ ...prev, visible: false })); } }
      if (key === '1' && !zhonyaStateRef.current.active && now > zhonyaStateRef.current.cooldownEnd) { zhonyaStateRef.current.active = true; zhonyaStateRef.current.endTime = now + GAME_CONFIG.ZHONYA_DURATION; zhonyaStateRef.current.cooldownEnd = now + GAME_CONFIG.ZHONYA_COOLDOWN; setIsZhonyaActive(true); setTargetPos(playerPosRef.current.clone()); setClickMarker(prev => ({ ...prev, visible: false })); forceUpdate(); }
    };
    window.addEventListener('keydown', handleKey); return () => window.removeEventListener('keydown', handleKey);
  }, [flashCooldownRef, wSkillEntity, wStateRef, zhonyaStateRef, isPaused, isGameOver, flashKey, selectedChamp, health, activeBuffs, setShowDamageFlash, forceUpdate, champConfig]); 

  const currentSpeedMult = activeBuffs.ghost ? GAME_CONFIG.GHOST_SPEED_MULT : 1.0;

  return (
    <>
      <color attach="background" args={['#202020']} /> 
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 50, 20]} intensity={1.5} castShadow />
      <Stars radius={100} depth={50} count={8000} factor={4} saturation={0} fade speed={1} />
      
      <Player 
        ref={playerPosRef} 
        targetPosition={targetPos} 
        flashTrigger={flashTrigger} 
        wTrigger={wTrigger} 
        selectedChamp={selectedChamp} 
        isGameOver={isGameOver} 
        isPaused={isPaused} 
        isZhonya={isZhonyaActive} 
        wMechanicsRef={wMechanicsRef}
        speedMult={currentSpeedMult} 
      />
      
      {karthusUlt && <KarthusUltEffect playerPosRef={playerPosRef} isPaused={isPaused} />}
      
      <JhinUltIndicator 
          key={jhinIndicator.id} 
          visible={jhinIndicator.visible} 
          position={jhinIndicator.position} 
          target={jhinIndicator.target} 
      />

      <MasteryEmote visible={showMastery} positionRef={playerPosRef} />
      <BansheeShield visible={activeBuffs.banshee} positionRef={playerPosRef} />
      <GhostEffect visible={activeBuffs.ghost} positionRef={playerPosRef} />

      {items.map(item => (<ItemOrb key={item.id} data={item} />))}

      <SkillMarker position={wMechanicsRef.current.shadowPos} visible={wSkillEntity.visible} type={wSkillEntity.type} color={selectedChamp === 'leblanc' ? '#9d00ff' : '#000'} />
      <WindWall position={wMechanicsRef.current.wallPos} rotation={wMechanicsRef.current.wallRot} visible={yasuoWall.visible} />
      
      {projectiles.map(p => ( 
          <Projectile 
              key={p.id} 
              data={p} 
              playerPosRef={playerPosRef} 
              activeWall={{ pos: wMechanicsRef.current.wallPos, rot: wMechanicsRef.current.wallRot, visible: yasuoWall.visible }} 
              onHit={handleHit} 
              onDespawn={removeProjectile} 
              isGameOver={isGameOver} 
              isPaused={isPaused} 
              isZhonya={isZhonyaActive}
              jhinData={jhinIndicator} 
          /> 
      ))}
      
      {xerathWs.map(w => ( <XerathW key={w.id} data={w} playerPosRef={playerPosRef} onHit={() => handleHit(1)} onComplete={removeXerathW} isPaused={isPaused} isGameOver={isGameOver} isZhonya={isZhonyaActive} /> ))}
      {luxUlts.map(l => ( <LuxUlt key={l.id} data={l} playerPosRef={playerPosRef} onHit={() => handleHit(1)} onComplete={removeLuxUlt} isPaused={isPaused} isGameOver={isGameOver} isZhonya={isZhonyaActive} /> ))}
      <ClickMarker key={clickMarker.key} position={clickMarker.pos} visible={clickMarker.visible} />
      
      <mesh 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, 0, 0]} 
        onPointerDown={onPointerDown} 
        onPointerMove={onPointerMove} 
        onContextMenu={(e) => e.nativeEvent.preventDefault()}
        visible={false} 
      >
        <planeGeometry args={[500, 500]} />
        <meshBasicMaterial visible={false} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]}>
         <planeGeometry args={[500, 500]} />
         <meshStandardMaterial color="#2a2a2a" roughness={0.8} />
      </mesh>
      
      <gridHelper args={[500, 200, "#444", "#222"]} rotation={[0, 0, 0]} position={[0, 0.01, 0]} />
    </>
  );
};

function App() {
  const [gameState, setGameState] = useState('MENU'); 
  const [selectedChamp, setSelectedChamp] = useState('leblanc');
  const [score, setScore] = useState(0);
  
  const [highScore, setHighScore] = useState(0);
  const [username, setUsername] = useState(() => localStorage.getItem('lol_dodge_username') || '');
  const [leaderboard, setLeaderboard] = useState([]);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); 

  const [isPaused, setIsPaused] = useState(false);
  // useReducer ile forceUpdate implementasyonu (setDummy yerine)
  const [updateIndex, forceUpdate] = useReducer((x) => x + 1, 0);

  const [flashKey, setFlashKey] = useState(() => localStorage.getItem('lol_dodge_flash_pref') || 'd');
  
  const [health, setHealth] = useState(GAME_CONFIG.MAX_HP);
  const [activeBuffs, setActiveBuffs] = useState({ banshee: false, ghost: false });
  const [showDamageFlash, setShowDamageFlash] = useState(false);
  
  const [gameKey, setGameKey] = useState(0);

  const [chatHistory, setChatHistory] = useState([{ sender: 'ai', text: "Selam brom! DestekMalDestek v3.0 devrede. Hazır mısın?" }]);
  
  const gameContext = { score, champ: selectedChamp, isGameOver: gameState === 'GAMEOVER', isPaused, hp: health };

  useEffect(() => { const handleEsc = (e) => { if (e.key === 'Escape' && gameState === 'PLAYING') setIsPaused(prev => !prev); }; window.addEventListener('keydown', handleEsc); return () => window.removeEventListener('keydown', handleEsc); }, [gameState]);
  useEffect(() => { localStorage.setItem('lol_dodge_flash_pref', flashKey); }, [flashKey]);
  useEffect(() => { localStorage.setItem('lol_dodge_username', username); }, [username]);

  // --- HATA DÜZELTME: champConfig ARTIK BURADA TANIMLI ---
  // Arayüz (HUD) hesaplamaları için champConfig gerekiyor.
  const champConfig = GAME_CONFIG.CHAMPIONS[selectedChamp];

  // --- FIREBASE LOGIC ---
  useEffect(() => {
      const fetchPersonalBest = async () => {
          if (!username.trim()) return;
          try {
              const docRef = doc(db, "scores", username.toLowerCase());
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                  setHighScore(docSnap.data().score);
              } else {
                  setHighScore(0);
              }
          } catch (error) {
              console.error("Skor çekilemedi:", error);
          }
      };
      fetchPersonalBest();
  }, [username, gameState]);

  useEffect(() => {
    if (gameState === 'MENU' || gameState === 'GAMEOVER') {
        fetchLeaderboard();
    }
  }, [gameState]);

  const fetchLeaderboard = async () => {
      try {
          const q = query(collection(db, "scores"), orderBy("score", "desc"), limit(10));
          const querySnapshot = await getDocs(q);
          const scores = [];
          querySnapshot.forEach((doc) => {
              scores.push({ id: doc.id, ...doc.data() });
          });
          setLeaderboard(scores);
      } catch (error) {
          console.error("Skor tablosu çekilemedi:", error);
      }
  };

  const saveScoreToFirebase = async (finalScore) => {
      if (!username.trim()) return; 
      
      setIsSubmittingScore(true);
      try {
          const userDocRef = doc(db, "scores", username.toLowerCase()); 
          const docSnap = await getDoc(userDocRef);

          if (!docSnap.exists() || finalScore > docSnap.data().score) {
              await setDoc(userDocRef, {
                  name: username,
                  score: finalScore,
                  champ: selectedChamp,
                  date: new Date().toISOString()
              });
              setHighScore(finalScore); 
          }
          fetchLeaderboard(); 
      } catch (error) {
          console.error("Skor kaydedilemedi:", error);
      } finally {
          setIsSubmittingScore(false);
      }
  };

  useEffect(() => {
      if (gameState === 'GAMEOVER') {
          saveScoreToFirebase(score);
      }
  }, [gameState]); 

  const handleDeleteScore = async (userId) => {
      if (!isAdmin) return;
      if (window.confirm(`${userId} kullanıcısının skorunu silmek istediğine emin misin?`)) {
          try {
              await deleteDoc(doc(db, "scores", userId));
              fetchLeaderboard(); 
              if (userId === username.toLowerCase()) setHighScore(0); 
          } catch (error) {
              console.error("Silme hatası:", error);
          }
      }
  };

  const handleAdminLogin = () => {
      const pass = prompt("Admin Şifresi:");
      if (pass === "Oçmami1234") {
          setIsAdmin(true);
          alert("Admin modu aktif.");
      } else {
          alert("Yanlış şifre.");
      }
  };

  const flashCooldownRef = useRef({ lastUsed: 0 });
  const wStateRef = useRef({ isOnCooldown: false, cooldownEnd: 0, activeState: false, stateExpire: 0 });
  const zhonyaStateRef = useRef({ active: false, endTime: 0, cooldownEnd: 0 });

  const now = Date.now();
  
  // UI Cooldown Calculations
  let wPct = 100, isWReady = true, wTimerText = null;
  if (wStateRef.current.activeState) { 
      wPct = (Math.max(0, wStateRef.current.stateExpire - now) / champConfig.W_DURATION) * 100; 
  } else if (wStateRef.current.isOnCooldown) { 
      isWReady = false; 
      const remainingCD = Math.max(0, wStateRef.current.cooldownEnd - now); 
      wPct = 100 - ((remainingCD / champConfig.W_COOLDOWN) * 100); 
      if (remainingCD <= 0) { 
          wStateRef.current.isOnCooldown = false; 
          isWReady = true; 
      } else { 
          wTimerText = (remainingCD / 1000).toFixed(1); 
      } 
  }
  
  const fElapsed = now - flashCooldownRef.current.lastUsed; 
  const fPct = Math.min(100, (fElapsed / GAME_CONFIG.FLASH_COOLDOWN) * 100); 
  const isFlashReady = fElapsed > GAME_CONFIG.FLASH_COOLDOWN; 
  const fTimerText = !isFlashReady ? ((GAME_CONFIG.FLASH_COOLDOWN - fElapsed) / 1000).toFixed(1) : null;
  
  const zRemaining = Math.max(0, zhonyaStateRef.current.cooldownEnd - now); 
  const isZhonyaReady = zRemaining <= 0; 
  const zPct = 100 - ((zRemaining / GAME_CONFIG.ZHONYA_COOLDOWN) * 100); 
  const zTimerText = !isZhonyaReady ? (zRemaining / 1000).toFixed(1) : null;

  // Render loop for UI updates when abilities are on cooldown
  useEffect(() => {
      if ((!isWReady && !wStateRef.current.activeState) || !isFlashReady || !isZhonyaReady) {
          const interval = setInterval(forceUpdate, 100);
          return () => clearInterval(interval);
      }
  }, [isWReady, isFlashReady, isZhonyaReady, updateIndex]); // updateIndex dependency ensures re-check

  const startGame = () => {
      if (!username.trim()) {
          alert("Brom başlamadan önce bi nick yaz şuraya!");
          return;
      }
      setGameKey(prev => prev + 1);
      setScore(0); setHealth(GAME_CONFIG.MAX_HP); setActiveBuffs({ banshee: false, ghost: false }); 
      setShowDamageFlash(false); 
      setGameState('PLAYING'); setIsPaused(false);
      wStateRef.current = { isOnCooldown: false, cooldownEnd: 0, activeState: false, stateExpire: 0 };
      zhonyaStateRef.current = { active: false, endTime: 0, cooldownEnd: 0 };
      flashCooldownRef.current = { lastUsed: 0 };
  };
  
  const showChat = gameState !== 'PLAYING' || isPaused;
  const highScoreLeague = getLeagueInfo(highScore);
  const currentScoreLeague = getLeagueInfo(score);

  return (
    <div style={{ width: '100%', height: '100%' }} onContextMenu={(e) => e.preventDefault()}>
      <AutoUpdater />
      <div className={`damage-overlay ${showDamageFlash ? 'active' : ''}`}></div>

      <ChatWidget visible={showChat} history={chatHistory} setHistory={setChatHistory} gameContext={gameContext} />

      {/* --- MENU --- */}
      {gameState === 'MENU' && (
          <div className="start-screen" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
              <div onClick={handleAdminLogin} style={{ position: 'absolute', bottom: '10px', left: '10px', color: '#333', cursor: 'pointer', zIndex: 100 }}>Admin</div>
              <div className="leaderboard-panel" style={{ position: 'absolute', left: '60px', top: '50%', transform: 'translateY(-50%)', height: '60vh', width: '300px', zIndex: 20 }}>
                  <div className="leaderboard-header"><h2 className="leaderboard-title" style={{fontSize: '1.4rem'}}>LİDERLER</h2></div>
                  <div className="leaderboard-list">
                      {leaderboard.length === 0 ? <div style={{color:'#888', textAlign:'center', marginTop:'20px', fontSize:'0.9rem'}}>Yükleniyor...</div> : leaderboard.map((player, idx) => (
                              <div key={idx} className={`leaderboard-item rank-${idx+1} ${player.name === username ? 'me' : ''}`} style={{padding:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                  <div style={{display: 'flex', gap: '10px'}}><span className="rank" style={{fontSize:'1.1rem'}}>{idx + 1}</span><span className="name" style={{fontSize:'1rem'}}>{player.name}</span></div>
                                  <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}><span className="score" style={{fontSize:'1.1rem', color: getLeagueInfo(player.score).color}}>{player.score}</span>{isAdmin && (<button onClick={() => handleDeleteScore(player.id)} style={{ background: 'red', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', padding: '2px 5px', fontSize: '0.8rem' }}>X</button>)}</div>
                              </div>
                          ))
                      }
                  </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 30, padding: '40px', borderRadius: '20px' }}>
                  <h1 className="game-title" style={{ marginBottom: '10px', fontSize: '6rem', textShadow: '0 0 40px rgba(200, 170, 110, 0.4)' }}>DODGE GAME</h1>
                  <div style={{marginBottom:'40px', textAlign: 'center'}}>
                      <div style={{color: '#888', fontSize: '1.1rem', marginBottom:'5px', letterSpacing:'3px'}}>EN İYİ SKORUN</div>
                      <div style={{fontSize: '4rem', fontFamily:'Impact', color: highScoreLeague.color, textShadow: highScoreLeague.glow ? '0 0 30px currentColor' : '0 0 15px rgba(0,0,0,0.8)', lineHeight: '1'}}>{highScore}</div>
                      <div style={{color: highScoreLeague.color, fontSize: '1.3rem', letterSpacing:'4px', marginTop:'5px', fontWeight:'bold'}}>{highScoreLeague.name}</div>
                  </div>
                  <div className="champ-select" style={{ marginBottom: '30px', gap: '40px' }}>{['leblanc', 'zed', 'yasuo'].map(c => ( <div key={c} className={`champ-btn ${selectedChamp === c ? 'selected' : ''}`} onClick={() => setSelectedChamp(c)} style={{ width: '110px', height: '110px', borderColor: c === 'leblanc' ? '#9d00ff' : c === 'zed' ? '#333' : '#0077ff' }}><span className="champ-icon" style={{fontSize: '3.5rem'}}>{getWIcon(c)}</span></div> ))}</div>
                  <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'20px', width: '100%'}}>
                      <input type="text" placeholder="Sihirdar Adı" value={username} maxLength={12} onChange={(e) => setUsername(e.target.value)} className="username-input" style={{ width: '320px', fontSize: '1.3rem', padding: '15px' }} />
                      <div style={{display:'flex', alignItems:'center', gap:'30px', marginTop:'10px'}}>
                          <div className="flash-toggle-container" style={{marginTop: '0'}}><div className={`toggle-switch ${flashKey === 'f' ? 'key-f' : ''}`} style={{width:'80px', height:'40px'}} onClick={() => setFlashKey(p => p === 'd' ? 'f' : 'd')}><span className={`toggle-text ${flashKey === 'd' ? 'active' : ''}`} style={{fontSize:'1.2rem'}}>D</span><div className="toggle-handle" style={{width:'30px', height:'30px'}}></div><span className={`toggle-text ${flashKey === 'f' ? 'active' : ''}`} style={{fontSize:'1.2rem'}}>F</span></div></div>
                          <button className="btn-start" onClick={startGame} style={{padding:'15px 60px', fontSize:'1.8rem', margin:'0'}}>BAŞLA</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* --- OYUN ICI HUD --- */}
      {gameState !== 'MENU' && (
        <div className="ui-layer">
            <div className="header">
                <div className="hud-container">
                    <div className="score-board">SCORE: {score}</div>
                    <div style={{ fontSize: '1.2rem', color: highScoreLeague.color, fontWeight: 'bold', marginTop: '-5px', textShadow: '0 0 5px #000' }}>BEST: {highScore} ({highScoreLeague.name})</div>
                    <div className="health-bar">{[...Array(GAME_CONFIG.MAX_HP)].map((_, i) => (<span key={i} className={`heart ${health <= i ? 'lost' : ''}`}>❤️</span>))}</div>
                    <div className="buff-container">{activeBuffs.banshee && <div className="buff-icon banshee">🛡️</div>}{activeBuffs.ghost && <div className="buff-icon ghost">👻</div>}</div>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <div className="skills">
                <div className={`skill-icon zhonya-skill ${isZhonyaReady ? 'ready' : ''} ${zhonyaStateRef.current.active ? 'active' : ''}`}><div className="skill-key">1</div><span style={{fontSize: '28px'}}>⏳</span>{!isZhonyaReady && !zhonyaStateRef.current.active && (<><div className="cooldown-overlay" style={{ height: `${100 - zPct}%` }}></div><span className="timer-text">{zTimerText}</span></>)}</div>
                <div className={`skill-icon w-skill ${wStateRef.current.activeState ? 'active' : (isWReady ? 'ready' : '')}`}><div className="skill-key">W</div><span style={{fontSize: '28px'}}>{getWIcon(selectedChamp)}</span>{!isWReady && !wStateRef.current.activeState && (<><div className="cooldown-overlay" style={{ height: `${100 - wPct}%` }}></div><span className="timer-text">{wTimerText}</span></>)}{wStateRef.current.activeState && (<div className="cooldown-overlay" style={{ height: `${100 - wPct}%`, opacity: 0.5, background: '#fff' }}></div>)}</div>
                <div className={`skill-icon d-skill ${isFlashReady ? 'ready' : ''}`}><div className="skill-key">{flashKey.toUpperCase()}</div><span style={{fontSize: '28px'}}>⚡</span>{!isFlashReady && (<><div className="cooldown-overlay" style={{ height: `${100 - fPct}%` }}></div><span className="timer-text">{fTimerText}</span></>)}</div>
            </div>
            </div>
        </div>
      )}

      {(isPaused && gameState === 'PLAYING') && (
          <div className="overlay-screen">
              <h1 style={{ color: '#fff', fontSize: '4rem' }}>PAUSED</h1>
              <div className="flash-toggle-container" style={{marginBottom: '20px'}}><div className="toggle-label">Flash Key</div><div className={`toggle-switch ${flashKey === 'f' ? 'key-f' : ''}`} onClick={() => setFlashKey(p => p === 'd' ? 'f' : 'd')}><span className={`toggle-text ${flashKey === 'd' ? 'active' : ''}`}>D</span><div className="toggle-handle"></div><span className={`toggle-text ${flashKey === 'f' ? 'active' : ''}`}>F</span></div></div>
              <button className="btn-restart" onClick={startGame}>RESTART</button>
              <button className="btn-restart" style={{marginTop:'10px', fontSize:'1rem'}} onClick={() => {setGameState('MENU'); setIsPaused(false);}}>MAIN MENU</button>
          </div>
      )}

      {gameState === 'GAMEOVER' && (
        <div className="overlay-screen">
          <h1 style={{ color: '#ff2244', fontSize: '5rem', fontFamily: 'Impact', marginBottom: '10px' }}>DEFEAT</h1>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <p style={{ color: '#ccc', fontSize: '2rem', margin: '5px' }}>Final Score: {score}</p>
              <p style={{ color: currentScoreLeague.color, fontSize: '2.5rem', fontFamily: 'Impact', margin: '5px', textShadow: currentScoreLeague.glow ? '0 0 20px currentColor' : '0 0 10px rgba(0,0,0,0.8)' }}>{currentScoreLeague.name}</p>
          </div>
          <div style={{marginBottom: '20px'}}>{isSubmittingScore ? <span style={{color: '#ffff00'}}>Skor sunucuya gönderiliyor...</span> : <span style={{color: '#00ff00'}}>Skor kaydedildi brom!</span>}</div>
          <div style={{ borderTop: '1px solid #555', paddingTop: '15px', marginTop: '10px', textAlign: 'center', width: '300px' }}><p style={{ color: '#ffd700', fontSize: '1.2rem', margin: '5px' }}>🏆 Your Best: {highScore}</p></div>
          <button className="btn-restart" onClick={startGame}>PLAY AGAIN</button>
          <button className="btn-restart" style={{marginTop:'10px', fontSize:'1rem'}} onClick={() => setGameState('MENU')}>MAIN MENU</button>
        </div>
      )}

      {gameState !== 'MENU' && (
        <div className="canvas-container">
            <Canvas shadows camera={{ position: [0, 45, 20], fov: 35 }}>
            <GameScene 
                key={gameKey}
                setScore={setScore} 
                setHighScore={setHighScore} 
                score={score} 
                highScore={highScore} 
                setGameOver={() => setGameState('GAMEOVER')} 
                setHealth={setHealth} 
                health={health} 
                activeBuffs={activeBuffs} 
                setActiveBuffs={setActiveBuffs} 
                setShowDamageFlash={setShowDamageFlash}
                flashCooldownRef={flashCooldownRef} 
                wStateRef={wStateRef} 
                zhonyaStateRef={zhonyaStateRef} 
                isPaused={isPaused} 
                isGameOver={gameState === 'GAMEOVER'} 
                flashKey={flashKey} 
                selectedChamp={selectedChamp} 
                forceUpdate={forceUpdate}
            />
            </Canvas>
        </div>
      )}
    </div>
  );
}

export default App;